import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { BudgetService } from '../../../services/budget.service';
import { BehaviorSubject, debounceTime, distinctUntilChanged, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { BudgetCategory } from '../../../models/budget-category.model';
import { GrupoItemResponse, ItembudgetCategory as ItembudgetCategoryDto } from '../../../dtos/grupo-itens-response.model';
import { ItembudgetCategory } from '../../../models/item-budget-category.model';
import { PaginationDto } from 'src/app/core/dto/pagination.dto';
import { ModaEditarComponent } from './components/moda-editar/moda-editar.component';
import { ItenGroupService } from '../../../services/iten-group.service';
import { ToastService } from 'src/app/shared/toasts/toast-service';
import Swal from 'sweetalert2';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BudgetItemCreateModalComponent } from '../budget-item-create-modal/budget-item-create-modal.component';


@Component({
  selector: 'app-budget-item-list',
  templateUrl: './budget-item-list.component.html',
  styleUrl: './budget-item-list.component.scss'
})
export class BudgetItemListComponent implements OnInit, OnDestroy {
  @Output() confirmEdit = new EventEmitter<any>();
  @ViewChild(ModaEditarComponent) modalEditar!: ModaEditarComponent;

  grupoIdAtual: string = "";
  private groupItemsSubject = new BehaviorSubject<GrupoItemResponse | null>(null);
  groupItems$ = this.groupItemsSubject.asObservable();

  breadCrumbItems!: Array<{}>;

  filtrarGrupoForm!: FormGroup;
  filtrarNomeForm!: FormGroup;
  private filterSubscription: Subscription = new Subscription();
  private destroy$ = new Subject<void>();
  private itemsSubscription?: Subscription;
  private atualizandoProgramaticamente = false; // Flag para evitar loops

  groups: BudgetCategory[] = []
  groupSelected$!: Observable<any>;
  isLoading = false;

  paginaAtualParaRecarregar?: number;
  grupoIdParaRegarregar?: string;
  paginacao: PaginationDto = {
    paginaAtual: 1,
    quantidadePorPagina: 10,
    totalDeItens: 0,
    ultimaPagina: 0,
  };

  constructor(
    public budgetService: BudgetService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private itenGroupService: ItenGroupService,
    private toastService: ToastService,
    private router: Router,
    private modalService: NgbModal
  ) {

    this.filtrarNomeForm = this.fb.group({
      "Paginacao.PaginaAtual": 1,
      "Paginacao.QuantidadePorPagina": 10,
      nome: [""],
      grupoid: [""]
    });
  }

  ngOnInit(): void {
    this.breadCrumbItems = [
      { label: "Projetos" },
      { label: "Itens de Orçamento", active: true },
    ];

    // form filtro busca nome
    const filters = this.budgetService.filters;
    this.filtrarGrupoForm = this.fb.group({
      "Paginacao.PaginaAtual": filters["PaginaAtual"],
      "Paginacao.QuantidadePorPagina": filters["QuantidadePorPagina"],
      nome: [""]
    });

    this.budgetService.getGroups().subscribe({
      next: (response) => {
        this.groups = response
      },
      error: (error) => {
        console.error('Erro ao buscar categorias:', error);
      },
      complete: () => {
        this.isLoading = false;
      }
    });

    this.onSubmit();

    //consulta quando filtra - apenas para o campo nome, com debounce
    this.filterSubscription = this.filtrarNomeForm.get('nome')?.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(
      (nome) => {
        if (!this.atualizandoProgramaticamente && this.grupoIdAtual) {
          this.budgetService.setFilters({ "Nome": nome || "" });
        }
      }
    ) || new Subscription();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.filterSubscription.unsubscribe();
    if (this.itemsSubscription) {
      this.itemsSubscription.unsubscribe();
    }
  }

  onSubmit() {
    const selectedGroupId = this.filtrarGrupoForm.value.nome;
    this.onFilter(selectedGroupId);
  }

  onFilter(id: string, paginaAtual?: number) {
    if (id && id.length > 0) {
      // Cancela subscrição anterior se existir
      if (this.itemsSubscription) {
        this.itemsSubscription.unsubscribe();
      }

      // Atualiza o grupoIdAtual e o formulário
      this.grupoIdAtual = id;
      this.atualizandoProgramaticamente = true;
      this.filtrarNomeForm.patchValue({ grupoid: id }, { emitEvent: false });

      // Define o GrupoId nos filtros do serviço ANTES de fazer a requisição
      // Importante: definir todos os filtros necessários
      const pagina = paginaAtual || this.paginacao.paginaAtual || 1;
      this.budgetService.setFilters({ 
        "GrupoId": id,
        "PaginaAtual": pagina,
        "QuantidadePorPagina": this.paginacao.quantidadePorPagina || 10,
        "Nome": this.filtrarNomeForm.value.nome || ""
      });

      this.isLoading = true;

      // O getGroupsItens observa o filterSubject com debounceTime
      // Como já definimos os filtros acima, o Observable pegará o valor atualizado
      this.itemsSubscription = this.budgetService.getGroupsItens(id).pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: response => {
          // Atualiza o subject com a resposta
          this.groupItemsSubject.next(response);

          // Atualiza paginação sem disparar eventos
          this.atualizandoProgramaticamente = true;
          this.paginacao.paginaAtual = paginaAtual == null ? response.paginacao.paginaAtual : paginaAtual;
          this.paginacao.quantidadePorPagina = response.paginacao.quantidadePorPagina;
          this.paginacao.totalDeItens = response.paginacao.totalDeItens;
          this.paginacao.ultimaPagina = response.paginacao.ultimaPagina;

          this.grupoIdParaRegarregar = response.grupoItem?.id;
          this.paginaAtualParaRecarregar = response.paginacao.paginaAtual;
          
          // Reset flag após um pequeno delay
          setTimeout(() => {
            this.atualizandoProgramaticamente = false;
          }, 100);
          
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Erro ao carregar itens:', error);
          this.toastService.show("Erro ao carregar itens do grupo", {
            classname: "bg-danger text-light",
          });
          this.isLoading = false;
          this.atualizandoProgramaticamente = false;
        },
        complete: () => {
          this.isLoading = false;
        }
      });
    } else {
      this.toastService.show("Selecione um grupo para filtrar", {
        classname: "bg-warning text-light",
      });
    }
  }

  onPageChange(page: number): void {
    // Evita loop: só atualiza se não estiver atualizando programaticamente
    if (!this.atualizandoProgramaticamente && this.grupoIdAtual) {
      this.onFilter(this.grupoIdAtual, page);
    }
  }

  openModalEditar(grupoId?: string, item?: ItembudgetCategoryDto, tipoDeCalculo?: number) {
    this.modalEditar.payload = { grupoId: grupoId, item: item, tipoDeCalculo: tipoDeCalculo };
    this.modalEditar.openModal();
  }
  // Evento de editar
  handleEdit(event: ItembudgetCategory): void {
    this.itenGroupService.updateItem(event).subscribe({
      next: (response) => {

        this.isLoading = true;

        this.toastService.show("Atualizado com sucesso!", {
          classname: "bg-success text-light",
        });

        this.confirmEdit.emit();

        this.recarregarListaPagainada();
      },
      error: (error) => {
        this.isLoading = false;

        this.toastService.show("Erro ao atualizar item!", {
          classname: "bg-danger text-light",
        });
      }
    })

    this.modalEditar.closeModal()
  }

  showDeleteConfirmation(id?: string) {
    this.cancel(id);
  }

  cancel(id?: string) {
    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: 'btn btn-success',
        cancelButton: 'btn btn-danger ms-2'
      },
      buttonsStyling: false
    });

    swalWithBootstrapButtons
      .fire({
        title: 'Continuar Excluindo?',
        text: 'Você não poderá reverter isso!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, excluir',
        cancelButtonText: 'Não, cancelar!'
      })
      .then(result => {
        if (result.value) {
          swalWithBootstrapButtons.fire(
            'Excluído!',
            'Seu item foi excluído.',
            'success'
          );

          if (id) {
            this.excluirItem(id);
          }

          this.recarregarListaPagainada();
        } else if (
          /* Read more about handling dismissals below */
          result.dismiss === Swal.DismissReason.cancel
        ) {
          swalWithBootstrapButtons.fire(
            'Cancelado',
            'Seu item continua seguro :)',
            'error'
          );
        }
      });
  }

  excluirItem(id: string): void {
    if (!id) {
      this.toastService.show("ID do item não fornecido para exclusão.", { classname: "bg-danger text-light" });
      return;
    }
    const itemParaExcluir: ItembudgetCategory = {
      id: id
    };
    this.itenGroupService.excluirItem(itemParaExcluir).subscribe({
      next: (response) => {
        this.toastService.show("Item excluído com sucesso!", {
          classname: "bg-success text-light",
        });
      },
      error: ((error) => {
        this.toastService.show("Erro ao excluir item!", {
          classname: "bg-danger text-light",
        });
      })
    })
  }

  recarregarListaPagainada(){
    if (this.grupoIdParaRegarregar != undefined)
      this.onFilter(this.grupoIdParaRegarregar, this.paginaAtualParaRecarregar)
  }

  novoItem(): void {
    const modalRef = this.modalService.open(BudgetItemCreateModalComponent, {
      size: 'lg',
      backdrop: 'static'
    });
    
    modalRef.result.then((result) => {
      if (result) {
        // Recarrega a lista se houver um grupo selecionado
        if (this.grupoIdAtual) {
          this.recarregarListaPagainada();
        }
      }
    }).catch(() => {
      // Modal foi fechada sem salvar
    });
  }

  formatarMetrosQuadrados(item: ItembudgetCategoryDto): string {
    const metroQuadrado = item?.metroQuadrado;
    const metroQuadradoAdicional = item?.metroQuadradoAdicional;
    
    // Se não existirem os valores, retorna "-"
    if ((metroQuadrado === undefined || metroQuadrado === null || metroQuadrado === 0) &&
        (metroQuadradoAdicional === undefined || metroQuadradoAdicional === null || metroQuadradoAdicional === 0)) {
      return "-";
    }
    
    // Formata os valores com 2 casas decimais
    const m2 = metroQuadrado !== undefined && metroQuadrado !== null && metroQuadrado !== 0 
      ? metroQuadrado.toFixed(2).replace('.', ',') 
      : '0,00';
    
    const m2Adicional = metroQuadradoAdicional !== undefined && metroQuadradoAdicional !== null && metroQuadradoAdicional !== 0
      ? metroQuadradoAdicional.toFixed(2).replace('.', ',')
      : '0,00';
    
    return `${m2} / ${m2Adicional}`;
  }
}
